'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import MobileMenu from './mobile-menu';
import AnimatedBorder from '@/components/ui/animated-border';
import { useTranslations } from 'next-intl';

const navigationItems = [
  { href: '/about-us', label: 'About Us' },
  { href: '/services', label: 'Services' },
  { href: '/integrations', label: 'Integrations' },
];

const HamburgerIcon = () => (
  <div className="flex flex-col justify-center items-end gap-[4px]">
    <span className="block rounded-full w-[22px] h-[1.5px] bg-white/70" />
    <span className="block rounded-full w-[22px] h-[1.5px] bg-white/70" />
    <span className="block rounded-full w-[15px] h-[1.5px] bg-white/70" />
    <span className="block rounded-full w-[11px] h-[1.5px] bg-white/70" />
  </div>
);

const MobileDock = () => {
  const t = useTranslations('accessibility');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showHamburger, setShowHamburger] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50;
      setVisible(window.scrollY > 80 && !atBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cycle: logo → hamburger → logo every 4s
  useEffect(() => {
    if (!visible) return;
    const cycle = () => {
      setShowHamburger(true);
      setTimeout(() => setShowHamburger(false), 1500);
    };
    const interval = setInterval(cycle, 4000);
    return () => clearInterval(interval);
  }, [visible]);

  return (
    <>
      <MobileMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        navigationItems={navigationItems}
      />

      <AnimatePresence>
        {visible && !isMenuOpen && (
          <motion.div
            className="fixed bottom-[24px] left-0 right-0 z-50 flex justify-center md:hidden pointer-events-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <div className="pointer-events-auto">
              <AnimatedBorder
                borderRadius="rounded-[22px]"
                borderWidth={1}
                borderColor="#8752FA"
                background="linear-gradient(180deg, rgba(28,26,55,0.92) 0%, rgba(18,17,36,0.98) 100%)"
                hoverBackground="linear-gradient(180deg, rgba(28,26,55,0.92) 0%, rgba(18,17,36,0.98) 100%)"
                speed={3}
                spread={80}
                cut={1}
                className="shadow-[0_8px_40px_rgba(0,0,0,0.5)] backdrop-blur-[20px]"
              >
                <div className="p-[5px]">
                  <button
                    onClick={() => setIsMenuOpen(true)}
                    className="relative flex items-center justify-center rounded-[17px] w-[56px] h-[56px] bg-[linear-gradient(180deg,_#3C1C63_0%,_rgba(60,28,99,0.4)_100%)] hover:bg-[linear-gradient(180deg,_#4d2680_0%,_rgba(60,28,99,0.6)_100%)] transition-all duration-300 outline-none focus-visible:ring-2 focus-visible:ring-theme/50 cursor-pointer"
                    aria-label={t('openMenu')}
                  >
                    <AnimatePresence mode="wait">
                      {showHamburger ? (
                        <motion.div
                          key="hamburger"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.3 }}
                          className="absolute"
                        >
                          <HamburgerIcon />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="logo"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.3 }}
                          className="absolute"
                        >
                          <img
                            src="/assets/brand/logo.svg"
                            alt="Flyingcode logo"
                            aria-hidden="true"
                            width={28}
                            height={28}
                            className="w-[28px] h-auto object-contain mix-blend-plus-lighter"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                </div>
              </AnimatedBorder>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileDock;
